// web/src/pages/admin/AccountTermsAdminPage.tsx
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { accountTermsApi, type AccountTermsSummaryDto, type CreateAccountTermsRequest } from "@/lib/clients-accounts";
import { TermsEditor } from "@/components/clients-accounts";
import { ListPage } from "@/archetypes/ListPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DetailsDrawer } from "@/components/details-drawer";
import { toast } from "@/hooks/use-toast";

export function AccountTermsAdminPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [text, setText] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["account-terms"], queryFn: () => accountTermsApi.list() });
  const createMut = useMutation({
    mutationFn: (b: CreateAccountTermsRequest) => accountTermsApi.create(b),
    onSuccess: () => {
      toast.success("Version created");
      setOpen(false); setTitle(""); setText("");
      void qc.invalidateQueries({ queryKey: ["account-terms"] });
    },
    onError: () => toast.error("Create failed"),
  });

  return (
    <ListPage
      title="Account terms versions"
      primaryAction={<Button onClick={() => setOpen(true)}>New version</Button>}
    >
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="px-2 py-1">Version</th>
              <th className="px-2 py-1">Effective from</th>
              <th className="px-2 py-1">Title</th>
              <th className="px-2 py-1">Created by</th>
              <th className="px-2 py-1 text-right">Accounts using</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((t: AccountTermsSummaryDto) => (
              <tr key={t.termsId} className="border-b">
                <td className="px-2 py-1">v{t.versionNumber}</td>
                <td className="px-2 py-1">{new Date(t.effectiveFrom).toLocaleDateString("en-AU")}</td>
                <td className="px-2 py-1">{t.title}</td>
                <td className="px-2 py-1">{t.createdByName ?? "—"}</td>
                <td className="px-2 py-1 text-right">{t.accountsUsingCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <DetailsDrawer open={open} onOpenChange={setOpen} title="New terms version">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            createMut.mutate({ title, effectiveFrom: new Date(effective).toISOString(), templateText: text });
          }}
        >
          <div><Label htmlFor="t-title">Title</Label><Input id="t-title" value={title} onChange={(e) => setTitle(e.target.value)} required /></div>
          <div><Label htmlFor="t-eff">Effective from</Label><Input id="t-eff" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} required /></div>
          <div><Label htmlFor="t-text">Template text</Label><TermsEditor value={text} onChange={setText} /></div>
          <Button type="submit" disabled={createMut.isPending || !title || !text}>{createMut.isPending ? "Saving…" : "Save"}</Button>
        </form>
      </DetailsDrawer>
    </ListPage>
  );
}
